import React, { useState, useEffect } from 'react'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { DataProvider } from './contexts/DataContext'
import TopBar from './components/TopBar'
import Kiosk from './pages/Kiosk'
import Personal from './pages/Personal'
import Admin from './pages/Admin'
import Builder from './pages/Builder'
import Export from './pages/Export'
import LoginModal from './components/LoginModal'
import SignupModal from './components/SignupModal'

function AppContent() {
  const { user, loading, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [showLogin, setShowLogin] = useState(false)
  const [showSignup, setShowSignup] = useState(false)
  const [allowAdminOnKiosk, setAllowAdminOnKiosk] = useState(false)

  // Handle route protection and redirection based on user role
  useEffect(() => {
    // Don't redirect while still loading user state
    if (loading) return
    
    const path = location.pathname
    
    if (user?.role === 'member' && path !== '/personal') {
      navigate('/personal', { replace: true })
    } else if (!user && (path === '/personal' || path === '/admin' || path === '/builder' || path === '/export')) {
      navigate('/', { replace: true })
    } else if (user?.role === 'admin' && path === '/' && !allowAdminOnKiosk) {
      // Only redirect admin to /admin if they haven't explicitly chosen to visit kiosk
      navigate('/admin', { replace: true })
    }
  }, [user, loading, location.pathname, navigate, allowAdminOnKiosk])

  // Reset the flag when user logs out
  useEffect(() => {
    if (!user) {
      setAllowAdminOnKiosk(false)
    }
  }, [user])

  const handleGoHome = () => {
    setAllowAdminOnKiosk(true)
    window.location.href = '/'
  }

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const getCurrentTab = () => {
    const path = location.pathname
    if (path === '/') return 'KIOSK'
    if (path === '/personal') return 'PERSONAL'
    if (path === '/admin') return 'ADMIN'
    if (path === '/builder') return 'BUILDER'
    if (path === '/export') return 'EXPORT'
    return 'KIOSK'
  }

  const handleTabChange = (tab) => {
    if (user?.role === 'member' && tab !== 'PERSONAL') return
    
    const routes = {
      'KIOSK': '/',
      'PERSONAL': '/personal',
      'ADMIN': '/admin',
      'BUILDER': '/builder',
      'EXPORT': '/export'
    }
    
    // Allow admin to access kiosk when explicitly navigating via tabs
    if (tab === 'KIOSK' && user?.role === 'admin') {
      setAllowAdminOnKiosk(true)
    }
    
    navigate(routes[tab] || '/')
  }

  const statusLabel = user?.role === 'member' 
    ? user.name.toUpperCase() 
    : (getCurrentTab() === 'KIOSK' ? 'KIOSK VIEW' : getCurrentTab())

  // Show loading state while authentication is being verified
  if (loading) {
    return (
      <div className="wrap" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'var(--accent)'
      }}>
        Loading...
      </div>
    )
  }

  return (
    <div>
      <TopBar 
        tab={getCurrentTab()}
        onTab={handleTabChange}
        onHome={handleGoHome}
        session={user}
        onLogout={handleLogout}
        showAdminLinks={user?.role === 'admin'}
        statusLabel={statusLabel}
        onShowLogin={() => setShowLogin(true)}
      />

      {/* Background for kiosk mode */}
      {getCurrentTab() === 'KIOSK' && (
        <div 
          className="kiosk-bg" 
          style={{ backgroundImage: 'url(/Backgound-01.png)' }} 
          aria-hidden="true"
        />
      )}

      <div className="wrap">
        <Routes>
          <Route path="/" element={<Kiosk onShowSignup={() => setShowSignup(true)} />} />
          <Route path="/personal" element={<Personal />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/builder" element={<Builder />} />
          <Route path="/export" element={<Export />} />
        </Routes>
      </div>

      {/* Modals */}
      {showLogin && (
        <LoginModal 
          onClose={() => setShowLogin(false)}
        />
      )}
      
      {showSignup && (
        <SignupModal 
          onClose={() => setShowSignup(false)}
        />
      )}
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <AppContent />
      </DataProvider>
    </AuthProvider>
  )
}

export default App
